import { jsx as _jsx } from "react/jsx-runtime";
// catmint/head/HeadContext — Context for collecting and managing head tags
import { createContext, useState, useCallback, useEffect, useRef, useMemo, } from "react";
/**
 * Merge multiple HeadConfig objects with proper override semantics:
 * - title: last one wins (child overrides parent)
 * - meta: deduplicate by name/property, last one wins
 * - link: append all
 */
export function mergeHeadConfigs(configs) {
    const merged = {};
    const metaMap = new Map();
    const links = [];
    for (const config of configs) {
        // Title: last wins
        if (config.title !== undefined) {
            merged.title = config.title;
        }
        // Meta: deduplicate by name or property
        if (config.meta) {
            for (const meta of config.meta) {
                const key = meta.name
                    ? `name:${meta.name}`
                    : meta.property
                        ? `property:${meta.property}`
                        : `content:${meta.content}`;
                metaMap.set(key, meta);
            }
        }
        // Link: append
        if (config.link) {
            links.push(...config.link);
        }
    }
    if (metaMap.size > 0) {
        merged.meta = Array.from(metaMap.values());
    }
    if (links.length > 0) {
        merged.link = links;
    }
    return merged;
}
export const HeadContext = createContext(null);
/**
 * Provider that manages head tag state for Head components and useHead hooks.
 */
export function HeadProvider({ children, initial, }) {
    const [stack, setStack] = useState(() => initial ? [initial] : []);
    const [hookConfigs, setHookConfigs] = useState([]);
    // Tracks the document.title that was in place before HeadProvider first
    // modified it.  Used to restore the layout's default title when navigating
    // to a page that does not export generateMetadata.  Initialised to `null`
    // and populated lazily inside the DOM effect (before the first write) so
    // that it captures the value React committed during hydration.
    const baselineTitleRef = useRef(null);
    // Whether we have ever written to document.title from this provider.
    const hasTouchedTitleRef = useRef(false);
    // When `initial` changes (e.g. soft navigation delivers new generateMetadata
    // results), reset the stack so the new base config takes effect.  Without
    // this, useState's initialiser only runs on mount and subsequent `initial`
    // values are silently ignored.
    const serializedInitial = JSON.stringify(initial);
    const prevInitialRef = useRef(serializedInitial);
    useEffect(() => {
        if (prevInitialRef.current !== serializedInitial) {
            prevInitialRef.current = serializedInitial;
            setStack(initial ? [initial] : []);
            setHookConfigs([]);
        }
    }, [serializedInitial, initial]);
    const push = useCallback((config) => {
        setStack((prev) => [...prev, config]);
    }, []);
    const remove = useCallback((config) => {
        setStack((prev) => prev.filter((c) => c !== config));
    }, []);
    const update = useCallback((config) => {
        setHookConfigs((prev) => {
            // Replace if same reference exists, otherwise append
            const idx = prev.findIndex((c) => c === config);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = config;
                return next;
            }
            return [...prev, config];
        });
    }, []);
    const mergedConfig = mergeHeadConfigs([...stack, ...hookConfigs]);
    // Memoize the context value so downstream effects that depend on `ctx`
    // don't re-fire on every HeadProvider render.  The push/remove/update
    // callbacks are already stable (useCallback with []), so we only need to
    // track `mergedConfig` — but since mergeHeadConfigs always returns a new
    // object, we serialise it for a stable identity check.
    const serializedConfig = JSON.stringify(mergedConfig);
    // ── Apply merged config to the real document <head> ──────────────────
    // Without this, the merged config only lives in React context.  Layout
    // components that render a plain `<title>` via JSX will overwrite
    // document.title during hydration, hiding the page-specific title from
    // generateMetadata.  This effect ensures the merged result always wins.
    useEffect(() => {
        if (typeof document === "undefined")
            return;
        const config = mergedConfig;
        // Title — set from config, or restore the baseline title (the value
        // document.title held before HeadProvider first modified it, i.e. the
        // layout's default title committed by React during hydration).
        if (config.title !== undefined) {
            // Capture baseline before our first write
            if (!hasTouchedTitleRef.current) {
                baselineTitleRef.current = document.title;
                hasTouchedTitleRef.current = true;
            }
            document.title = config.title;
        }
        else if (hasTouchedTitleRef.current &&
            baselineTitleRef.current !== null) {
            // Merged config no longer specifies a title — revert to the layout's
            // default that was in place before we first touched it.
            document.title = baselineTitleRef.current;
        }
        // Meta tags — upsert by name/property, mark with data-catmint-head
        // Build a set of active meta keys so we can clean up stale ones afterward.
        const activeMetaSelectors = new Set();
        if (config.meta) {
            for (const meta of config.meta) {
                const selector = meta.name
                    ? `meta[name="${meta.name}"]`
                    : meta.property
                        ? `meta[property="${meta.property}"]`
                        : null;
                if (selector) {
                    activeMetaSelectors.add(selector);
                    let el = document.head.querySelector(selector);
                    if (!el) {
                        el = document.createElement("meta");
                        if (meta.name)
                            el.setAttribute("name", meta.name);
                        if (meta.property)
                            el.setAttribute("property", meta.property);
                        document.head.appendChild(el);
                    }
                    el.setAttribute("content", meta.content);
                    el.setAttribute("data-catmint-head", "true");
                }
            }
        }
        // Remove stale catmint-managed meta tags that are no longer in the config
        const allCatmintMeta = document.head.querySelectorAll('meta[data-catmint-head="true"]');
        for (const el of allCatmintMeta) {
            const name = el.getAttribute("name");
            const property = el.getAttribute("property");
            const selector = name
                ? `meta[name="${name}"]`
                : property
                    ? `meta[property="${property}"]`
                    : null;
            if (selector && !activeMetaSelectors.has(selector)) {
                el.remove();
            }
        }
        // Link tags — upsert by rel+href, mark with data-catmint-head
        // Build a set of active link keys so we can clean up stale ones afterward.
        const activeLinkKeys = new Set();
        if (config.link) {
            for (const linkDef of config.link) {
                const { rel, href, ...attrs } = linkDef;
                const linkKey = `${rel}|${href}`;
                activeLinkKeys.add(linkKey);
                let el = document.head.querySelector(`link[rel="${rel}"][href="${href}"][data-catmint-head="true"]`);
                if (!el) {
                    el = document.createElement("link");
                    el.setAttribute("rel", rel);
                    el.setAttribute("href", href);
                    el.setAttribute("data-catmint-head", "true");
                    document.head.appendChild(el);
                }
                for (const [key, value] of Object.entries(attrs)) {
                    el.setAttribute(key, value);
                }
            }
        }
        // Remove stale catmint-managed link tags that are no longer in the config
        const allCatmintLinks = document.head.querySelectorAll('link[data-catmint-head="true"]');
        for (const el of allCatmintLinks) {
            const rel = el.getAttribute("rel") ?? "";
            const href = el.getAttribute("href") ?? "";
            if (!activeLinkKeys.has(`${rel}|${href}`)) {
                el.remove();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serializedConfig]);
    const contextValue = useMemo(() => ({
        config: mergedConfig,
        push,
        remove,
        update,
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serializedConfig, push, remove, update]);
    return (_jsx(HeadContext.Provider, { value: contextValue, children: children }));
}
//# sourceMappingURL=HeadContext.js.map